/* KBW — the assembly option, the behaviour half.

   Two screens use it — the cabinet list and the product page — and they have
   different cart code, so this file owns the CONTROL and hands the cart lines
   back rather than posting anything itself. One implementation of the rule,
   two callers.

   WHAT IT DOES
   1. Reveals the control. The markup is printed hidden (kbw-assembly.css) and
      revealed by the `is-asm-ready` class this file adds, because the tick is
      worth nothing without script: the assembly line has to be posted beside
      the cabinet AND at the same quantity, and neither can be expressed in a
      plain form whose fields are fixed at render time. A tick that silently did
      nothing would be worse than no tick at all.
   2. Toggles what the ticked state says — the arithmetic line on a list row,
      the description, the arithmetic and the button's figure on a product page.
      Every one of those strings is rendered server-side in both states and only
      swapped here, so the figures are Liquid's `money` output and this file
      never does arithmetic or currency formatting.
   3. Answers `KbwAssembly.extra(scope, quantity)` with the cart lines the
      caller should post alongside the cabinet.

   MARKUP CONTRACT
     [data-kbw-asm-root]            carries data-asm-variant / data-asm-prop for the page
     [data-kbw-asm-scope]           one cabinet: a list row, or the product form
       [data-kbw-asm-tick]            the checkbox
       [data-kbw-asm-for]             (attribute) the cabinet's name for the cart
       [data-kbw-asm-variant]         (attribute) the cabinet's variant id, so the
                                      cart can tie a surcharge to its cabinet
       [data-kbw-asm-sum]             shown only while ticked
       [data-kbw-asm-off]             shown only while NOT ticked
       [data-kbw-asm-opt]             the box that fills when ticked
       [data-kbw-asm-cta]             a button whose words change with the state;
                                      data-on / data-off carry the two strings
*/

(function () {
  'use strict';

  function scopeOf(el) {
    return el.closest('[data-kbw-asm-scope]');
  }

  function paint(scope) {
    var tick = scope.querySelector('[data-kbw-asm-tick]');
    if (!tick) return;
    var on = tick.checked;

    scope.querySelectorAll('[data-kbw-asm-sum]').forEach(function (n) { n.hidden = !on; });
    scope.querySelectorAll('[data-kbw-asm-off]').forEach(function (n) { n.hidden = on; });
    scope.querySelectorAll('[data-kbw-asm-opt]').forEach(function (n) { n.classList.toggle('is-on', on); });

    scope.querySelectorAll('[data-kbw-asm-cta]').forEach(function (n) {
      var next = on ? n.getAttribute('data-on') : n.getAttribute('data-off');
      /* A button with no second string keeps the words it was printed with —
         an empty setting must not blank the only control that adds to cart. */
      if (next) n.textContent = next;
    });
  }

  function setup(root) {
    if (root.dataset.kbwAsmReady === '1') return;
    root.dataset.kbwAsmReady = '1';
    root.classList.add('is-asm-ready');

    root.addEventListener('change', function (e) {
      var tick = e.target.closest ? e.target.closest('[data-kbw-asm-tick]') : null;
      if (!tick) return;
      var scope = scopeOf(tick);
      if (scope) paint(scope);
    });

    /* Browsers restore checkbox state across a back-navigation without firing
       `change`, so the page would come back ticked and silent about it. */
    root.querySelectorAll('[data-kbw-asm-scope]').forEach(paint);
  }

  function setupAll() {
    document.querySelectorAll('[data-kbw-asm-root]').forEach(setup);
  }

  window.KbwAssembly = {
    /* The cart lines to post ALONGSIDE the cabinet. An empty array when the
       feature is off, when this cabinet cannot be assembled, or when the
       customer did not ask for it — so a caller can always concatenate the
       answer without testing anything first.

       The quantity matches the cabinet's: one assembly per cabinet, which is
       what the product page promises in so many words. */
    extra: function (el, quantity) {
      if (!el) return [];
      /* Callers hand over whatever they have — a form, a row, the buy box — so
         the scope is resolved here rather than being a thing each caller has to
         know. `closest` matches the element itself, so a caller that already
         has the scope loses nothing. */
      var scope = el.closest('[data-kbw-asm-scope]');
      if (!scope) return [];
      var tick = scope.querySelector('[data-kbw-asm-tick]');
      if (!tick || !tick.checked) return [];

      var root = scope.closest('[data-kbw-asm-root]');
      if (!root) return [];

      var variant = root.getAttribute('data-asm-variant');
      if (!variant) return [];

      var q = parseInt(quantity, 10) || 1;
      var line = { id: Number(variant), quantity: q };

      /* Three properties, doing three different jobs.

         The visible one names the cabinet: a cart with six lines all called
         "Cabinet assembly" is unreadable, and this is also what the warehouse
         reads off the order, so it is the one that must not be hidden.

         `_kbw_assembly` marks the line as a surcharge and `_kbw_for` says which
         cabinet variant it belongs to. Both start with an underscore, which is
         how Shopify keeps a property out of the cart, the checkout and the
         order — they exist for the cart snippets, not for anybody to read. The
         cart is drawn from the MARKER rather than from today's fee variant, so
         a cart that already holds a surcharge still reads correctly if assembly
         is switched off tomorrow. */
      var name = scope.getAttribute('data-kbw-asm-for') || '';
      var prop = root.getAttribute('data-asm-prop') || '';
      var forVariant = scope.getAttribute('data-kbw-asm-variant') || '';

      line.properties = { _kbw_assembly: '1' };
      if (name && prop) line.properties[prop] = name;
      if (forVariant) line.properties._kbw_for = forVariant;

      return [line];
    },

    /* So a caller can skip building a payload it will not use. */
    isOn: function (el) {
      if (!el) return false;
      var scope = el.closest('[data-kbw-asm-scope]');
      if (!scope) return false;
      var tick = scope.querySelector('[data-kbw-asm-tick]');
      return !!(tick && tick.checked);
    },

    refresh: setupAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAll);
  } else {
    setupAll();
  }

  document.addEventListener('shopify:section:load', setupAll);
})();
